import { Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { CRITERION_IMPORTANCE_LABELS, CRITERION_KIND_LABELS, libraryNameOf, type SavedCriterionView } from '@nest-recruit/shared';
import { Button } from '../../../../components/Button';
import { Checkbox } from '../../../../components/Checkbox';
import { SearchInput } from '../../../../components/Input';
import { SkeletonLines } from '../../../../components/Skeleton';
import { errorMessage } from '../../../../lib/api-client';
import { useToasts } from '../../../../stores/toasts';
import { useCriteriaLibrary, useRemoveFromLibrary } from '../../api/criteria-library-api';
import styles from './builder.module.css';

/**
 * F5.23 "Add from saved" (ADR 0048): tick criteria saved from earlier jobs and each is copied into
 * this one, to be edited freely here. Anchored to its button (CLAUDE.md §5). A name already in
 * this job can't be added twice.
 */

interface CriteriaLibraryPickerProps {
  /** The names already in this job's table. */
  readonly existingLabels: readonly string[];
  /** How many more this job can take (20 at most). */
  readonly room: number;
  readonly onAdd: (picked: readonly SavedCriterionView[]) => void;
  readonly onCancel: () => void;
}

export function CriteriaLibraryPicker({ existingLabels, room, onAdd, onCancel }: CriteriaLibraryPickerProps): ReactNode {
  const library = useCriteriaLibrary(true);
  const remove = useRemoveFromLibrary();
  const show = useToasts((toasts) => toasts.show);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<ReadonlySet<string>>(new Set());
  const taken = useMemo(() => new Set(existingLabels.map(libraryNameOf)), [existingLabels]);
  const items = (library.data ?? []).filter((item) => matches(item, query));
  const chosen = (library.data ?? []).filter((item) => picked.has(item.id));
  const isFull = chosen.length >= room;

  function toggle(id: string, isOn: boolean): void {
    setPicked((current) => {
      const next = new Set(current);
      if (isOn) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  function removeSaved(item: SavedCriterionView): void {
    toggle(item.id, false);
    remove.mutate(item, {
      onSuccess: () => show({ tone: 'success', message: `"${item.label}" removed from the library`, detail: 'Jobs that already copied it keep their copy.' }),
      onError: (cause) => show({ tone: 'danger', message: `"${item.label}" wasn't removed`, detail: errorMessage(cause) }),
    });
  }

  return (
    <div className={styles.library}>
      <p className={styles.libraryTitle}>Add saved criteria</p>
      <SearchInput placeholder="Search saved criteria" value={query} autoFocus onChange={(event) => setQuery(event.target.value)} onClear={() => setQuery('')} aria-label="Search saved criteria" />
      {library.isPending ? (
        <SkeletonLines count={4} />
      ) : (library.data ?? []).length === 0 ? (
        <p className={styles.help}>Nothing saved yet. In any job's criteria, open a row's ⋯ menu and choose "Save to library".</p>
      ) : items.length === 0 ? (
        <p className={styles.help}>No saved criterion matches "{query}".</p>
      ) : (
        <ul className={styles.libraryList}>
          {items.map((item) => {
            const isTaken = taken.has(libraryNameOf(item.label));
            const isPicked = picked.has(item.id);
            return (
              <li key={item.id} className={styles.libraryItem}>
                <Checkbox
                  checked={isPicked}
                  disabled={isTaken || (isFull && !isPicked)}
                  label={item.label}
                  onChange={(isOn) => toggle(item.id, isOn)}
                />
                <span className={styles.libraryMeta}>
                  {isTaken ? 'Already in this job' : describe(item)}
                </span>
                <Button variant="ghost" size="sm" icon={Trash2} aria-label={`Remove ${item.label} from the library`} onClick={() => removeSaved(item)} />
              </li>
            );
          })}
        </ul>
      )}
      {room <= 0 && <p className={styles.help}>This job already has 20 criteria, the most it can take.</p>}
      <div className={styles.libraryActions}>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="solid" size="sm" disabled={chosen.length === 0} onClick={() => onAdd(chosen)}>
          {chosen.length === 0 ? 'Add' : `Add ${chosen.length}`}
        </Button>
      </div>
    </div>
  );
}

function describe(item: SavedCriterionView): string {
  const words = item.lookFor.length > 0 ? ` · ${item.lookFor.slice(0, 4).join(', ')}${item.lookFor.length > 4 ? '…' : ''}` : '';
  return `${CRITERION_IMPORTANCE_LABELS[item.importance]} · ${CRITERION_KIND_LABELS[item.kind]}${words}`;
}

function matches(item: SavedCriterionView, query: string): boolean {
  const text = query.trim().toLowerCase();
  if (text === '') return true;
  return item.label.toLowerCase().includes(text) || item.lookFor.some((word) => word.toLowerCase().includes(text));
}
